import { MaintenancePointApi } from 'api/MaintenancePointApi'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { StationApi } from 'api/StationApi'
import { Point } from 'geojson'
import { maintenancePointMapIconId } from 'pages/Assets/maintenance/maintenancePointUtils'
import { resolveStationMapIconIdFromLots } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import { useEffect, useState } from 'react'
import { ILot, IMaintenancePoint, IRobotInfo, IStation } from 'utils/interfaces'

export const FACILITY_DIALOG_MAP_ICON = 'dialog-facility-v3'

export type FacilityDialogContextPin = {
  id: string
  kind: 'facility' | 'station' | 'maintenance'
  point: Point
  iconId: string
}

function asPoint(raw: { type?: string; coordinates?: number[] } | Point | null | undefined): Point | null {
  if (!raw || raw.type !== 'Point' || !Array.isArray(raw.coordinates) || raw.coordinates.length < 2) {
    return null
  }
  const lng = Number(raw.coordinates[0])
  const lat = Number(raw.coordinates[1])
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null
  }
  return { type: 'Point', coordinates: [lng, lat] }
}

/**
 * Loads facility geo + geo-bound stations / MPs / robots for SP create/edit dialog maps (UX-DIALOG-GEO).
 */
export function useFacilityDialogGeoContext(
  facilityId: number | null | undefined,
  enabled: boolean,
  options?: {
    excludeStationId?: number | null
    excludeMaintenancePointId?: number | null
  }
): {
  facilityGeoPoint: Point | null
  contextPins: FacilityDialogContextPin[]
  robots: IRobotInfo[]
  loading: boolean
} {
  const [facilityGeoPoint, setFacilityGeoPoint] = useState<Point | null>(null)
  const [contextPins, setContextPins] = useState<FacilityDialogContextPin[]>([])
  const [robots, setRobots] = useState<IRobotInfo[]>([])
  const [loading, setLoading] = useState(false)

  const excludeStationId = options?.excludeStationId ?? null
  const excludeMaintenancePointId = options?.excludeMaintenancePointId ?? null

  useEffect(() => {
    if (!enabled || !facilityId) {
      setFacilityGeoPoint(null)
      setContextPins([])
      setRobots([])
      setLoading(false)
      return
    }

    let cancelled = false
    // Drop previous facility context immediately so camera/pins do not stick on the old site.
    setFacilityGeoPoint(null)
    setContextPins([])
    setRobots([])
    setLoading(true)

    const load = async () => {
      try {
        const [facility, stations, maintenancePoints, lots] = await Promise.all([
          MainApi.fetchFacility(facilityId),
          StationApi.fetchStations({ facilityId }),
          MaintenancePointApi.fetchMaintenancePoints(facilityId),
          StationApi.fetchLots(),
        ])

        if (cancelled) {
          return
        }

        const facilityPoint = asPoint(facility?.geo_point)
        setFacilityGeoPoint(facilityPoint)

        const lotsByStationId = new Map<number, ILot[]>()
        for (const lot of lots) {
          const list = lotsByStationId.get(lot.robot_station) ?? []
          list.push(lot)
          lotsByStationId.set(lot.robot_station, list)
        }

        const pins: FacilityDialogContextPin[] = []
        if (facilityPoint) {
          pins.push({
            id: `facility-${facilityId}`,
            kind: 'facility',
            point: facilityPoint,
            iconId: FACILITY_DIALOG_MAP_ICON,
          })
        }

        for (const station of stations as IStation[]) {
          if (excludeStationId != null && station.id === excludeStationId) {
            continue
          }
          const point = asPoint(station.point)
          if (!point) {
            continue
          }
          pins.push({
            id: `station-${station.id}`,
            kind: 'station',
            point,
            iconId: resolveStationMapIconIdFromLots(lotsByStationId.get(station.id) ?? []),
          })
        }

        for (const mp of maintenancePoints as IMaintenancePoint[]) {
          if (excludeMaintenancePointId != null && mp.id === excludeMaintenancePointId) {
            continue
          }
          const point = asPoint(mp.point)
          if (!point) {
            continue
          }
          pins.push({
            id: `mp-${mp.id}`,
            kind: 'maintenance',
            point,
            iconId: maintenancePointMapIconId(mp.type),
          })
        }

        setContextPins(pins)

        const robotIds: number[] = Array.isArray(facility?.robots_ids) ? facility.robots_ids : []
        if (robotIds.length === 0) {
          setRobots([])
        } else {
          const { results } = await RobotApi.fetchRobots(1, 1000, undefined, robotIds, undefined, undefined, true)
          if (!cancelled) {
            setRobots(results.filter(robot => Boolean(robot.lastPosition)))
          }
        }
      } catch (error) {
        console.warn('[useFacilityDialogGeoContext] failed to load facility geo context', error)
        if (!cancelled) {
          setFacilityGeoPoint(null)
          setContextPins([])
          setRobots([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [enabled, facilityId, excludeStationId, excludeMaintenancePointId])

  return { facilityGeoPoint, contextPins, robots, loading }
}
